import { CalendaHelper } from "@/Appointments/Helpers/CalendarHelper";
import { CalendarDisplay } from "@/Appointments/Models/Settings/CalendarDisplay";
import { CalendarStore } from "@/Appointments/Stores/CalendarStore";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { RoomUIModel } from "@/Common/Models/UI/RoomUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class CalendarDisplaySettingsHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Calendar.Checkbox_Change, this.OnCheckboxChange);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_ChangeTimeResolution, this.OnTimeResolutionChange);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForDay, this.OnMaximumCalendarsShownForDayChange);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForWeek, this.OnMaximumCalendarsShownForWeekChange);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForMonth, this.OnMaximumCalendarsShownForMonthChange);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_EmployeeFilterChange, this.OnEmployeeFilterChange);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_Ok, this.OnDisplaySettingsOkClick);
        this.RegisterOperation(LocalOperations.Calendar.DisplaySettings_Cancel, this.OnDisplaySettingsCancelClick);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.OnContainerClick, false);
    }

    private OnCheckboxChange(request: LocalEvent, store: CalendarStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");
        var q = store.calendarDisplaySettingsModel.get("EmployeesSearchText");

        switch (target) {
            case CheckboxTarget.Myle_Calendar_PatientsAreVisible:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("PatientsAreVisible", checked);
                break;
            case CheckboxTarget.Myle_Calendar_RoomsAreVisible:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsAreVisible", checked);
                break;
            case CheckboxTarget.Myle_Calendar_StatusAreVisible:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("StatusAreVisible", checked);
                break;
            case CheckboxTarget.Myle_Calendar_PhonesAreVisible:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("ContactPointsAreVisible", checked);
                break;
            case CheckboxTarget.Myle_Calendar_IncludeCanceled:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("IncludeCanceled", checked);
                break;
            case CheckboxTarget.Myle_Calendar_IncludeNAM:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("IncludeNAM", checked);
                break;
            case CheckboxTarget.Myle_Calendar_SitesAreVisible:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("SitesAreVisible", checked);
                break;
            case CheckboxTarget.Myle_Calendar_TriageScoreVisible:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("CanadianTriageAndAcuityScoreVisible", checked);
                break;
            case CheckboxTarget.Myle_Calendar_OneLineViewSelected:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("OneLineViewSelected", checked);
                break;
            case CheckboxTarget.Myle_Calendar_SaveSettingsSelected:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("SaveSettingsSelected", checked);
                break;
            case CheckboxTarget.Myle_Calendar_SearchSites:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("IsAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.calendarDisplaySettingsModel.get("Sites"), checked);
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("Sites", sites);
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q));
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsFiltered", store.GetRoomsFromSelectedSitesInDisplaySettings());
                break;
            case CheckboxTarget.Myle_Calendar_SiteItem:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.calendarDisplaySettingsModel.get("Sites"), id, checked));

                // uncheck anybody who is not in the selected sites
                var admissibleEmployees = CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q).toArray().map(x => x.Id);
                var idsToUncheck = store.calendarDisplaySettingsModel.get("EmployeesGlobalCache").toArray().filter(x => admissibleEmployees.indexOf(x.Id) == -1).map(x => x.Id);
                this.helper.FindUpdateICheckableItems(store.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), idsToUncheck, false);

                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q));
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsFiltered", store.GetRoomsFromSelectedSitesInDisplaySettings());
                break;
            case CheckboxTarget.Myle_Calendar_SearchEmployees:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("IsAllEmployeesChecked", checked);
                var list = CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q);
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), listOfClinicianIds, checked);
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesGlobalCache", c);
                var checkedList = CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q);
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", checkedList);
                break;
            case CheckboxTarget.Myle_Calendar_EmployeeItem:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesGlobalCache", store.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), id, checked));
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q));
                break;
            case CheckboxTarget.Myle_Calendar_SearchRooms:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("IsAllRoomsChecked", checked);
                store.helper.ChangeCheckStateFromAllICheckable(store.calendarDisplaySettingsModel.get("Rooms"), checked);
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsFiltered", store.GetRoomsFromSelectedSitesInDisplaySettings());
                break;
            case CheckboxTarget.Myle_Calendar_RoomItem:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("Rooms", store.helper.FindUpdateICheckableItem<RoomUIModel>(store.calendarDisplaySettingsModel.get("Rooms"), id, checked));
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsFiltered", store.GetRoomsFromSelectedSitesInDisplaySettings());
                break;
            case CheckboxTarget.Myle_Calendar_SearchEquipments:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("IsAllEquipmentChecked", checked);
                break;
            case CheckboxTarget.Myle_Calendar_CollapseRulesets:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("AreRulesetsCollapsed", checked);
                break;
            case CheckboxTarget.Myle_Calendar_OnlySelectedSites:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("ShowOnlySelectedSites", checked);
                break;
            case CheckboxTarget.Myle_Calendar_SortByScheduleSelectedEmployees:
                store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("SortByScheduleSelectedEmployees", checked);
                break;
            default:
                break;
        }
    }

    private OnTimeResolutionChange(request: LocalEvent, store: CalendarStore): void {
        var value = request.Get<number>("value");
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("TimeSlotLength", value);
    }

    private OnMaximumCalendarsShownForDayChange(request: LocalEvent, store: CalendarStore): void {
        const value = request.Get<number>("value");
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("MaximumCalendarsShownForDay", value);
    }

    private OnMaximumCalendarsShownForWeekChange(request: LocalEvent, store: CalendarStore): void {
        const value = request.Get<number>("value");
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("MaximumCalendarsShownForWeek", value);
    }

    private OnMaximumCalendarsShownForMonthChange(request: LocalEvent, store: CalendarStore): void {
        const value = request.Get<number>("value");
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("MaximumCalendarsShownForMonth", value);
    }

    private OnEmployeeFilterChange(request: LocalEvent, store: CalendarStore): void {
        var val = request.Get<string>("value");

        var list = CalendaHelper.GetEmployeesBasedOnSitesAndQuery(val);

        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesSearchText", val);
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", list);
    }

    private OnDisplaySettingsOkClick(request: LocalEvent, store: CalendarStore): void {
        // save settings if checkbox enabled
        if (store.calendarDisplaySettingsModel.get("SaveSettingsSelected")) {
            store.calendarActions.SaveDisplaySettings(store.calendarDisplaySettingsModel);
            store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("SaveSettingsSelected", false);
        }

        cacheStore.UserSettings.CalendarDisplay = CalendarDisplay.FromModel(store.calendarDisplaySettingsModel);

        store.calendarModel = store.calendarModel.set("ShowCalendarDisplaySettings", false);
        store.SetCalendarEmployees();
    }

    private OnDisplaySettingsCancelClick(request: LocalEvent, store: CalendarStore): void {
        store.calendarDisplaySettingsModel = store.undoCalendarDisplaySettings;
        store.undoCalendarDisplaySettings = null;
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("Sites", this.helper.ChangeCheckStateFromAllICheckable(store.calendarDisplaySettingsModel.get("Sites"), false));
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", this.helper.ChangeCheckStateFromAllICheckable(store.calendarDisplaySettingsModel.get("EmployeesFiltered"), false));
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsFiltered", this.helper.ChangeCheckStateFromAllICheckable(store.calendarDisplaySettingsModel.get("RoomsFiltered"), false));
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("Sites", this.helper.FindUpdateICheckableItems(store.calendarDisplaySettingsModel.get("Sites"), store.undoSelectedSiteIds, true));
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("EmployeesFiltered", this.helper.FindUpdateICheckableItems(store.calendarDisplaySettingsModel.get("EmployeesFiltered"), store.undoSelectedEmployeesIds, true));
        store.calendarDisplaySettingsModel = store.calendarDisplaySettingsModel.set("RoomsFiltered", this.helper.FindUpdateICheckableItems(store.calendarDisplaySettingsModel.get("RoomsFiltered"), store.undoSelectedRoomIds, true));

        store.calendarModel = store.calendarModel.set("ShowCalendarDisplaySettings", false);
    }

    private OnContainerClick(request: LocalEvent, store: CalendarStore): void {
        if (store.calendarModel.get("ShowCalendarDisplaySettings") === true) {
            this.OnDisplaySettingsOkClick(request, store);
            store.EmitChangeToListeners();
        }
    }
}

export { CalendarDisplaySettingsHandler };
